import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, RefreshCw, Check, Pencil } from "lucide-react";
import { api } from "../lib/api";
import logoMark from "../assets/logo-mark.png";

/**
 * "My Freedom From Back Pain Identity" — onboarding step shown after sign-in
 * when the user hasn't yet created one. Two questions → AI-generated cinematic
 * portrait via Gemini Nano Banana.
 */
export default function IdentityPage() {
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(null); // existing record, if any
  const [stage, setStage] = useState("loading"); // loading | intro | form | generating | reveal | error
  const [who, setWho] = useState("");
  const [what, setWhat] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/identity").then((r) => {
      if (cancelled) return;
      const d = r.data;
      if (d?.avatar) {
        setIdentity(d);
        setWho(d.who || "");
        setWhat(d.what || "");
        setStage("reveal");
      } else if (d?.who) {
        // partial — show form pre-filled
        setWho(d.who);
        setWhat(d.what || "");
        setStage("intro");
      } else {
        setStage("intro");
      }
    }).catch(() => setStage("intro"));
    return () => { cancelled = true; };
  }, []);

  async function submit() {
    if (who.trim().length < 3 || what.trim().length < 3) {
      setErr("Tell yourself something honest — even one full sentence is enough.");
      return;
    }
    setErr("");
    setStage("generating");
    try {
      const { data } = await api.post("/identity", {
        who: who.trim(),
        what: what.trim(),
        generate_avatar: true,
      });
      sessionStorage.removeItem("identity-dismissed");
      setIdentity(data);
      setStage(data.avatar ? "reveal" : "error");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save right now. Try again.");
      setStage("form");
    }
  }

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      {/* Cinematic ambient backdrop */}
      <div className="absolute inset-0">
        <div className="bps-ambient" />
        <motion.div
          className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(34,211,238,0.32), transparent 70%)" }}
          animate={{ x: [0, 40, -20, 0], y: [0, 30, -20, 0], opacity: [0.55, 0.9, 0.55] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-40 -right-24 w-[460px] h-[460px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(245,158,11,0.28), transparent 70%)" }}
          animate={{ x: [0, -30, 20, 0], y: [0, -20, 25, 0], opacity: [0.4, 0.75, 0.4] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
          }}
        />
      </div>

      <div className="relative mx-auto w-full max-w-md min-h-screen px-5 pt-8 pb-12 flex flex-col">
        <AnimatePresence mode="wait">
          {stage === "loading" && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex items-center justify-center"
            >
              <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#22D3EE] animate-spin" />
            </motion.div>
          )}

          {stage === "intro" && (
            <IntroStage
              key="intro"
              onBegin={() => setStage("form")}
              onSkip={() => navigate("/home")}
            />
          )}

          {stage === "form" && (
            <FormStage
              key="form"
              who={who} setWho={setWho}
              what={what} setWhat={setWhat}
              err={err}
              onSubmit={submit}
              onBack={() => setStage("intro")}
            />
          )}

          {stage === "generating" && <GeneratingStage key="gen" />}

          {stage === "reveal" && identity && (
            <RevealStage
              key="reveal"
              identity={identity}
              onContinue={() => navigate("/home")}
              onRegenerate={() => setStage("form")}
            />
          )}

          {stage === "error" && (
            <ErrorStage
              key="err"
              onRetry={() => setStage("form")}
              onSkip={() => navigate("/home")}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ───────────────────────── stages ───────────────────────── */

function IntroStage({ onBegin, onSkip }) {
  function dismiss() {
    sessionStorage.setItem("identity-dismissed", "1");
    onSkip();
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.7 }}
      className="flex-1 flex flex-col"
    >
      <div className="flex items-center gap-3 mb-8">
        <img src={logoMark} alt="" className="w-10 h-10 rounded-2xl object-cover drop-shadow-[0_0_18px_rgba(34,211,238,0.45)]" />
        <div className="bps-eyebrow text-[#22D3EE]">Before we begin</div>
      </div>

      <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter leading-[1.05] text-white">
        My Freedom From <br />
        Back Pain <br />
        Identity.
      </h1>

      <p className="text-sm text-white/85 mt-6 leading-relaxed max-w-sm">
        Before the work begins, picture a version of you that is already
        <span className="text-white"> free from pain</span>. Two questions.
        Honest answers. We'll bring that person to life.
      </p>

      <div className="mt-10 space-y-3">
        <Pill index={1} text="Who would you be without back pain?" />
        <Pill index={2} text="What would you do if you didn't suffer?" />
      </div>

      <div className="mt-auto pt-12 space-y-3">
        <button
          type="button"
          onClick={onBegin}
          data-testid="identity-begin"
          className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold inline-flex items-center justify-center gap-2 shadow-[0_0_24px_rgba(34,211,238,0.4)] hover:-translate-y-0.5 transition-all"
        >
          Begin the imagining <ArrowRight size={16} strokeWidth={2.2} />
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="identity-skip-intro"
          className="w-full py-3 text-sm text-[#9AAEC6] hover:text-white transition-colors"
        >
          Maybe later
        </button>
      </div>
    </motion.div>
  );
}

function Pill({ index, text }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full border border-[#22D3EE]/55 text-[#22D3EE] font-mono text-[11px] font-semibold shrink-0">
        {index}
      </span>
      <span className="text-base text-white/90 leading-snug">{text}</span>
    </div>
  );
}

function FormStage({ who, setWho, what, setWhat, err, onSubmit, onBack }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.6 }}
      className="flex-1 flex flex-col"
    >
      <button
        onClick={onBack}
        className="text-xs font-mono uppercase tracking-[0.22em] text-[#7C8FA8] hover:text-white transition-colors mb-6 self-start"
        data-testid="identity-back-to-intro"
      >
        ← Back
      </button>

      <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tighter leading-tight text-white">
        Picture <span className="text-[#22D3EE]">future you</span>.
      </h1>
      <p className="text-sm text-white/70 mt-3 leading-relaxed">
        There are no wrong answers. Write what your body already knows.
      </p>

      <div className="mt-7 space-y-5">
        <div>
          <label className="bps-eyebrow text-[#22D3EE] mb-2 inline-block">
            01 · Who would I be without back pain?
          </label>
          <textarea
            value={who}
            onChange={(e) => setWho(e.target.value)}
            rows={4}
            placeholder="Someone playful, present, fearless in my own body…"
            data-testid="identity-who"
            className="w-full rounded-2xl bg-[#03101F]/70 border border-white/10 focus:border-[#22D3EE]/60 focus:outline-none focus:ring-2 focus:ring-[#22D3EE]/15 px-4 py-3 text-sm text-white placeholder:text-[#5A6E87]/70 leading-relaxed resize-none transition-all"
          />
        </div>

        <div>
          <label className="bps-eyebrow text-[#F59E0B] mb-2 inline-block">
            02 · What would I do if I didn't suffer?
          </label>
          <textarea
            value={what}
            onChange={(e) => setWhat(e.target.value)}
            rows={4}
            placeholder="Hike again. Pick up my child. Travel without dread. Run."
            data-testid="identity-what"
            className="w-full rounded-2xl bg-[#03101F]/70 border border-white/10 focus:border-[#F59E0B]/60 focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/15 px-4 py-3 text-sm text-white placeholder:text-[#5A6E87]/70 leading-relaxed resize-none transition-all"
          />
        </div>

        {err && <div className="text-xs text-[#F87171]" data-testid="identity-error">{err}</div>}
      </div>

      <div className="mt-auto pt-10">
        <button
          type="button"
          onClick={onSubmit}
          data-testid="identity-submit"
          className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold inline-flex items-center justify-center gap-2 shadow-[0_0_24px_rgba(34,211,238,0.4)] hover:-translate-y-0.5 transition-all"
        >
          Generate my freedom identity <Sparkles size={15} />
        </button>
        <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] text-center mt-3">
          Takes about 15 seconds · powered by Gemini Nano Banana
        </p>
      </div>
    </motion.div>
  );
}

function GeneratingStage() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex-1 flex flex-col items-center justify-center text-center"
      data-testid="identity-generating"
    >
      {/* Animated halo */}
      <div className="relative w-40 h-40 mb-8">
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{ background: "conic-gradient(from 0deg, #22D3EE, transparent 50%, #F59E0B, transparent 100%)", filter: "blur(8px)" }}
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-3 rounded-full bg-[#0A1530] flex items-center justify-center"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          <Sparkles size={30} className="text-[#22D3EE]" strokeWidth={1.6} />
        </motion.div>
      </div>

      <div className="bps-eyebrow text-[#22D3EE]">Generating</div>
      <h2 className="font-display text-2xl font-light tracking-tighter mt-2 max-w-xs">
        Bringing the version of you that lives without pain into focus.
      </h2>
      <p className="text-xs text-[#9AAEC6] mt-4 font-mono uppercase tracking-[0.22em]">
        Please hold · about 15 seconds
      </p>
    </motion.div>
  );
}

function RevealStage({ identity, onContinue, onRegenerate }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.02 }}
      transition={{ duration: 0.9 }}
      className="flex-1 flex flex-col"
      data-testid="identity-reveal"
    >
      <div className="flex items-center gap-3 mb-5">
        <img src={logoMark} alt="" className="w-9 h-9 rounded-2xl object-cover drop-shadow-[0_0_14px_rgba(34,211,238,0.4)]" />
        <div className="bps-eyebrow text-[#22D3EE]">Your freedom identity</div>
      </div>

      {/* Portrait */}
      <div className="relative mx-auto w-full">
        <motion.div
          className="absolute inset-0 rounded-3xl blur-2xl"
          style={{ background: "radial-gradient(circle at 30% 30%, rgba(34,211,238,0.45), transparent 60%), radial-gradient(circle at 70% 70%, rgba(245,158,11,0.30), transparent 60%)" }}
          animate={{ opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <div
          className="relative rounded-3xl overflow-hidden border border-[#22D3EE]/30"
          style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.55), 0 0 32px rgba(34,211,238,0.25)" }}
        >
          {identity?.avatar ? (
            <img
              src={identity.avatar}
              alt="Your freedom identity"
              data-testid="identity-avatar"
              className="w-full aspect-square object-cover"
              draggable={false}
            />
          ) : (
            <div className="w-full aspect-square flex items-center justify-center text-sm text-[#9AAEC6]">
              No avatar yet
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <QuoteCard accent="#22D3EE" label="Who I am without pain" body={identity?.who} />
        <QuoteCard accent="#F59E0B" label="What I do free from pain" body={identity?.what} />
      </div>

      <div className="mt-7 space-y-3">
        <button
          type="button"
          onClick={onContinue}
          data-testid="identity-continue"
          className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold inline-flex items-center justify-center gap-2 shadow-[0_0_24px_rgba(34,211,238,0.4)] hover:-translate-y-0.5 transition-all"
        >
          <Check size={16} strokeWidth={2.4} /> Carry this with me
        </button>
        <button
          type="button"
          onClick={onRegenerate}
          data-testid="identity-regenerate"
          className="w-full py-3 inline-flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#9AAEC6] hover:text-white transition-colors"
        >
          <Pencil size={11} /> Edit answers · regenerate portrait
        </button>
      </div>
    </motion.div>
  );
}

function QuoteCard({ accent, label, body }) {
  return (
    <div className="bps-card p-4">
      <div className="bps-eyebrow mb-1.5" style={{ color: accent }}>{label}</div>
      <p className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap">{body}</p>
    </div>
  );
}

function ErrorStage({ onRetry, onSkip }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1 flex flex-col items-center justify-center text-center"
      data-testid="identity-error-stage"
    >
      <div className="bps-eyebrow text-[#F87171]">Something interrupted</div>
      <h2 className="font-display text-2xl font-light tracking-tighter mt-2 max-w-xs">
        We couldn't bring your portrait to life right now.
      </h2>
      <p className="text-sm text-[#9AAEC6] mt-3 leading-relaxed max-w-xs">
        Your words are saved. Try again in a moment — sometimes the AI needs a quiet breath too.
      </p>
      <div className="mt-8 space-y-3 w-full max-w-sm">
        <button
          type="button"
          onClick={onRetry}
          className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold inline-flex items-center justify-center gap-2"
        >
          <RefreshCw size={14} /> Try again
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="w-full py-3 text-sm text-[#9AAEC6] hover:text-white transition-colors"
        >
          Continue without portrait
        </button>
      </div>
    </motion.div>
  );
}
